/**
 * GA drawing cache — memory for this tab, Cache Storage for the rest of the day.
 *
 * First open still hits the network. After that, reopen / scroll-back / refresh
 * reads the same bytes from disk until local midnight (or the drawing changes).
 */

import { sameLocalDay, gaPersistUrl } from './gaDrawingCacheKeys';

const MAX_DRAWINGS = 8;
const MAX_PAGES = 80;
const CACHE_NAME = 'lgl-ga-drawings-v1';

const drawings = new Map(); // id -> { url, mime, promise }
const previews = new Map(); // id -> { url, promise }
const pages = new Map(); // `${id}:${page}` -> { url, mime, promise }

export { sameLocalDay, gaPersistUrl };

function bump(map, id, entry) {
  map.delete(id);
  map.set(id, entry);
}

function evictOldest(map, max) {
  if (map.size <= max) return;
  for (const [id, entry] of map) {
    if (map.size <= max) return;
    if (!entry?.url) continue;
    try { window.URL.revokeObjectURL(entry.url); } catch { /* ignore */ }
    map.delete(id);
  }
}

function persistAvailable() {
  return typeof window !== 'undefined' && typeof window.caches?.open === 'function';
}

async function readPersist(url) {
  if (!persistAvailable()) return null;
  try {
    const cache = await window.caches.open(CACHE_NAME);
    const res = await cache.match(url);
    if (!res) return null;
    const storedAt = Number(res.headers.get('X-GA-Cached-At') || 0);
    if (storedAt && !sameLocalDay(storedAt)) {
      await cache.delete(url);
      return null;
    }
    const blob = await res.blob();
    if (!blob || !blob.size) return null;
    return { blob, mime: res.headers.get('Content-Type') || blob.type || 'application/octet-stream' };
  } catch {
    return null;
  }
}

async function writePersist(url, blob, mime) {
  if (!persistAvailable() || !blob) return;
  try {
    const cache = await window.caches.open(CACHE_NAME);
    await cache.put(url, new Response(blob, {
      headers: {
        'Content-Type': mime || blob.type || 'application/octet-stream',
        'X-GA-Cached-At': String(Date.now()),
      },
    }));
  } catch { /* private mode / quota */ }
}

async function forgetPersist(dashboardId) {
  if (!persistAvailable()) return;
  try {
    const cache = await window.caches.open(CACHE_NAME);
    const keys = await cache.keys();
    const id = String(dashboardId);
    await Promise.all(
      keys
        .filter((req) => {
          try {
            const parts = new URL(req.url).pathname.split('/').filter(Boolean);
            return parts[1] === id;
          } catch {
            return false;
          }
        })
        .map((req) => cache.delete(req))
    );
  } catch { /* ignore */ }
}

function blobFetcher(authenticatedApiCall, instance, accounts) {
  return (path) => authenticatedApiCall(instance, accounts, 'GET', path, null, { responseType: 'blob' });
}

export function makeGaBlobFetcher(authenticatedApiCall, instance, accounts) {
  return blobFetcher(authenticatedApiCall, instance, accounts);
}

function toBlob(data, mime) {
  if (data instanceof Blob) {
    return data.type ? data : new Blob([data], { type: mime });
  }
  return new Blob([data], { type: mime });
}

function rememberDrawing(id, blob, mime, promise) {
  const url = window.URL.createObjectURL(blob);
  const entry = { url, mime, promise };
  bump(drawings, id, entry);
  evictOldest(drawings, MAX_DRAWINGS);
  return entry;
}

function rememberPage(id, blob, mime, promise) {
  const url = window.URL.createObjectURL(blob);
  const entry = { url, mime, promise };
  bump(pages, id, entry);
  evictOldest(pages, MAX_PAGES);
  return entry;
}

export function getCachedGaDrawing(dashboardId) {
  const hit = drawings.get(String(dashboardId));
  return hit?.url ? hit : null;
}

export function getCachedGaPreview(dashboardId) {
  const hit = previews.get(String(dashboardId));
  return hit?.url ? hit : null;
}

export function getCachedGaDrawingPage(dashboardId, pageNumber) {
  const hit = pages.get(`${dashboardId}:${Math.max(1, parseInt(pageNumber, 10) || 1)}`);
  return hit?.url ? hit : null;
}

export function prefetchGaDrawing(fetchBlob, dashboardId, opts = {}) {
  const id = String(dashboardId);
  const hit = drawings.get(id);
  if (hit?.promise) {
    bump(drawings, id, hit);
    return hit.promise;
  }
  const persistUrl = gaPersistUrl('drawing', id, null, opts.version);
  const promise = (async () => {
    const persisted = await readPersist(persistUrl);
    if (persisted) return rememberDrawing(id, persisted.blob, persisted.mime, promise);
    const res = await fetchBlob(`/ga-dashboards/dashboards/${id}/drawing`);
    const mime = res.headers?.['content-type'] || 'application/octet-stream';
    const blob = toBlob(res.data, mime);
    writePersist(persistUrl, blob, mime);
    return rememberDrawing(id, blob, mime, promise);
  })().catch((err) => {
    drawings.delete(id);
    throw err;
  });
  drawings.set(id, { url: null, mime: null, promise });
  return promise;
}

export function prefetchGaDrawingPage(fetchBlob, dashboardId, pageNumber, opts = {}) {
  const page = Math.max(1, parseInt(pageNumber, 10) || 1);
  const id = `${dashboardId}:${page}`;
  const hit = pages.get(id);
  if (hit?.promise) {
    bump(pages, id, hit);
    return hit.promise;
  }
  const persistUrl = gaPersistUrl('page', dashboardId, page, opts.version);
  const promise = (async () => {
    const persisted = await readPersist(persistUrl);
    if (persisted) return rememberPage(id, persisted.blob, persisted.mime, promise);
    const res = await fetchBlob(`/ga-dashboards/dashboards/${dashboardId}/drawing?page=${page}`);
    const mime = res.headers?.['content-type'] || 'application/pdf';
    const blob = toBlob(res.data, mime);
    writePersist(persistUrl, blob, mime);
    return rememberPage(id, blob, mime, promise);
  })().catch((err) => {
    pages.delete(id);
    throw err;
  });
  pages.set(id, { url: null, mime: null, promise });
  return promise;
}

export function forgetGaDrawing(dashboardId) {
  const id = String(dashboardId);
  const draw = drawings.get(id);
  if (draw?.url) {
    try { window.URL.revokeObjectURL(draw.url); } catch { /* ignore */ }
  }
  drawings.delete(id);
  const prev = previews.get(id);
  if (prev?.url) {
    try { window.URL.revokeObjectURL(prev.url); } catch { /* ignore */ }
  }
  previews.delete(id);
  for (const key of [...pages.keys()]) {
    if (key === id || key.startsWith(`${id}:`)) {
      const entry = pages.get(key);
      if (entry?.url) {
        try { window.URL.revokeObjectURL(entry.url); } catch { /* ignore */ }
      }
      pages.delete(key);
    }
  }
  forgetPersist(id);
}

export function prefetchGaPreview(fetchBlob, dashboardId, opts = {}) {
  const id = String(dashboardId);
  const hit = previews.get(id);
  if (hit?.promise) {
    bump(previews, id, hit);
    return hit.promise;
  }
  const persistUrl = gaPersistUrl('preview', id, null, opts.version);
  const promise = (async () => {
    const persisted = await readPersist(persistUrl);
    if (persisted) {
      const url = window.URL.createObjectURL(persisted.blob);
      const entry = { url, promise };
      bump(previews, id, entry);
      return entry;
    }
    const res = await fetchBlob(`/ga-dashboards/dashboards/${id}/thumbnail?size=hq&v=3`);
    const blob = toBlob(res.data, 'image/jpeg');
    writePersist(persistUrl, blob, 'image/jpeg');
    const url = window.URL.createObjectURL(blob);
    const entry = { url, promise };
    bump(previews, id, entry);
    return entry;
  })().catch((err) => {
    previews.delete(id);
    throw err;
  });
  previews.set(id, { url: null, promise });
  return promise;
}

export function isSequenceDashboard(dashboard) {
  const n = parseInt(dashboard?.settings?.pageCount, 10);
  return Number.isFinite(n) && n > 1;
}

/** After the first page is up, quietly pull the rest so later opens this day are instant. */
export function warmSequencePages(fetchBlob, dashboardId, pageCount, opts = {}) {
  const total = Math.max(1, parseInt(pageCount, 10) || 1);
  let next = 1;
  let inflight = 0;
  const pump = () => {
    while (inflight < 2 && next <= total) {
      const n = next;
      next += 1;
      inflight += 1;
      prefetchGaDrawingPage(fetchBlob, dashboardId, n, opts)
        .catch(() => {})
        .finally(() => {
          inflight -= 1;
          if (next <= total) setTimeout(pump, 60);
        });
    }
  };
  pump();
}

export function prefetchDashboardDrawing(fetchBlob, dashboard) {
  if (!dashboard?.id || !dashboard.drawing_path) return Promise.resolve(null);
  const version = dashboard.updated_at;
  prefetchGaPreview(fetchBlob, dashboard.id, { version }).catch(() => {});
  if (isSequenceDashboard(dashboard)) {
    return prefetchGaDrawingPage(fetchBlob, dashboard.id, 1, { version });
  }
  return prefetchGaDrawing(fetchBlob, dashboard.id, { version });
}
